// src/lib/composite-subject.ts
//
// ★★★ WAVE 12 (P4) — the compound-subject convention for the three composite-key
// catalogues, in one place.
//
// `VersionHistory` offers ONE code picker and serves all thirteen administered
// catalogues. Three of them are keyed by a PAIR — agency mappings
// (`mappingAgencyCode` + `providerAgencyCode`), HSP group memberships
// (`hspAgencyCode` + `groupCode`) and MBIM records (`officerId` +
// `entitlementType`) — and their series routes genuinely need both keys: called
// with only the first, the route answers with the CURRENT BOARD rather than one
// pair's history, which would render plausibly under a "Version history" heading.
//
// ══ ⚠ WHY THIS IS ITS OWN MODULE AND NOT PART OF `lib/api.ts` ══
//
// It was in `api.ts` for about ten minutes and **eleven DOM tests went red**. Those
// suites `vi.mock('../lib/api')`, which replaces the WHOLE module — so a page
// importing a pure string helper from it got `undefined` and failed to render, and
// the failures pointed at published-bounds assertions rather than at the import.
//
// The deeper reason is the one that makes this the right file rather than a
// workaround: `api.ts` is the TRANSPORT boundary. These two functions do no I/O,
// know no route and touch no `fetch` — putting them there forced every test that
// stubs the network to also stub string formatting. A module that must be mocked
// for the wrong reasons is a module with two jobs.
//
// ⚠ The separator is `' / '` **with spaces**, not `'/'`, `':'` or `'-'`. Every key
// involved is an agency, group or scheme code from a `^[A-Z][A-Z0-9_]*$`-shaped
// domain, so none can contain a space — which makes the separator unambiguous
// AGAINST THE DATA rather than merely unlikely. A bare `'/'` would also need
// escaping inside a URL path, which is the bug `encodeURIComponent` exists to stop.

/** Build the compound subject the picker offers. Inverse of {@link splitCompositeSubject}. */
export function compositeSubject(first: string, second: string): string {
  return `${first} / ${second}`
}

/**
 * Read a compound subject back into its two keys, or `undefined` if it is not a
 * pair.
 *
 * ⚠ **`undefined` rather than a best guess**, so a caller cannot silently request a
 * one-key series and render an agency's current board as one pair's history. The
 * three pages branch on it and fetch NOTHING when it is absent.
 */
export function splitCompositeSubject(subject: string): { readonly first: string; readonly second: string } | undefined {
  const parts = subject.split(' / ')
  if (parts.length !== 2) return undefined
  const [first, second] = parts
  if (first === undefined || second === undefined || first.length === 0 || second.length === 0) return undefined
  return { first, second }
}
